import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import styled from "styled-components/native"

import { StripeProrationAvailableResponse } from "@treefort/api-spec"
import { DisplayableError } from "@treefort/lib/displayable-error"
import { getCurrencyDivisor } from "@treefort/lib/money"
import { formatMoney } from "@treefort/lib/money"

import { useCheckoutSession } from "../hooks/use-checkout-session"
import { logError } from "../lib/logging"
import { Button } from "./button"
import Column from "./column"
import ExpandableInfo from "./expandable-info"
import Modal from "./modal"
import Row from "./row"
import Text from "./text"

const HorizontalRule = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.border.primary};
`

const LineItemTitle = styled(Text)`
  flex: 1;
`

const LineItemAmount = styled(Text)`
  flex-shrink: 0;
`

function LineItem({
  title,
  currency,
  amount,
  paddingLeft,
}: {
  title: string
  currency: string
  amount: number
  paddingLeft?: Parameters<typeof Row>[0]["paddingLeft"]
}) {
  const { i18n } = useTranslation()
  return (
    <Row
      alignItems="flex-end"
      justifyContent="space-between"
      gap="xsmall"
      paddingLeft={paddingLeft}
    >
      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
      <LineItemTitle textStyle="caption" color="secondary">
        {title}:
      </LineItemTitle>
      <LineItemAmount textStyle="caption" color="secondary">
        {formatMoney(
          {
            currency,
            amount,
            divisor: getCurrencyDivisor(currency),
          },
          i18n.language,
        )}
      </LineItemAmount>
    </Row>
  )
}

/**
 * NOTE: the modal won't open if the preview prop isn't provided
 */
export default function StripeProrationPreviewModal({
  open,
  onClose,
  title,
  action,
  preview,
}: {
  open: boolean
  onClose: () => void
  title?: string
  action?: () => Promise<void>
  preview?: StripeProrationAvailableResponse
}): React.JSX.Element {
  const checkoutSession = useCheckoutSession()
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const isLoading =
    checkoutSession?.type === "paidPlanWithProrationPreview" &&
    checkoutSession.plan.id === preview?.subscriptionPlanId
  const totalDiscount = preview?.totalDiscountAmounts.reduce(
    (sum, discount) => sum + discount.amount,
    0,
  )
  const { t, i18n } = useTranslation()

  // If something goes wrong it can be helpful to allow the user to press the
  // subscribe button again when they come back to the app, even if we have an
  // in progress checkout session, but we also want to gaurd against the user
  // double pressing the subscribe button
  useEffect(() => {
    if (!isLoading) {
      setIsCheckingOut(false)
    }
  }, [isLoading])

  return (
    <Modal
      type="sheet"
      open={Boolean(open && preview)}
      onPressOutside={onClose}
      onPressCloseButton={onClose}
      title={title}
    >
      {preview ? (
        <Column padding="medium" alignItems="stretch" gap="medium">
          <Text textStyle="headingLarge" alignment="center">
            {t("Due Now: {{amount}}", {
              amount: formatMoney(
                {
                  currency: preview.currency,
                  amount: preview.amountDue,
                  divisor: getCurrencyDivisor(preview.currency),
                },
                i18n.language,
              ),
            })}
          </Text>
          <Column gap="tiny" alignItems="stretch">
            <ExpandableInfo
              headerContent={
                <LineItem
                  currency={preview.currency}
                  title={t("Subtotal")}
                  amount={preview.subtotal}
                />
              }
            >
              <Column gap="tiny" alignItems="stretch">
                {preview.lineItems.map((item) => (
                  <LineItem
                    currency={preview.currency}
                    title={item.description}
                    amount={item.amount}
                    key={item.description}
                  />
                ))}
              </Column>
            </ExpandableInfo>
            <LineItem
              currency={preview.currency}
              title={t("Tax")}
              amount={preview.tax}
              paddingLeft="medium"
            />
            {totalDiscount ? (
              <LineItem
                currency={preview.currency}
                amount={-totalDiscount}
                title={t("Discount")}
                paddingLeft="medium"
              />
            ) : null}
            {preview.startingBalance ? (
              <LineItem
                currency={preview.currency}
                amount={preview.startingBalance}
                title={t("Balance")}
                paddingLeft="medium"
              />
            ) : null}
            <HorizontalRule />
            <LineItem
              currency={preview.currency}
              amount={
                preview.endingBalance < 0
                  ? preview.endingBalance
                  : preview.amountDue
              }
              title={t("Total")}
              paddingLeft="medium"
            />
          </Column>
          <Button
            id="stripe-proration-preview-modal-subscribe"
            type="primary"
            loading={isLoading}
            onPress={async () => {
              if (!isCheckingOut) {
                setIsCheckingOut(true)
                try {
                  action?.()
                } catch (e) {
                  logError(
                    new DisplayableError(
                      t("An error occurred. Please try again."),
                      e,
                    ),
                  )
                }
                setIsCheckingOut(false)
              }
            }}
          >
            {t("Subscribe")}
          </Button>
        </Column>
      ) : null}
    </Modal>
  )
}
